/**
 * Words for the Free plan's personal sync limits (backend
 * Plans.PersonalSyncQuota): the server rejects an op with
 * `plan_quota_boards`, `plan_quota_documents` or `plan_quota_storage` — and,
 * for document workspaces (Plans.DocumentScopeQuota), `plan_quota_document_scopes`
 * — and a raw code in a notification reads like a crash.
 */

const LIMIT_TEXT = {
  plan_quota_boards: 'You have reached the number of boards your plan syncs.',
  plan_quota_documents: 'You have reached the number of documents your plan syncs.',
  plan_quota_storage: 'You have reached the storage your plan syncs.',
  plan_quota_document_scopes: 'You have reached the number of document workspaces your plan syncs.',
};

const WHAT_TO_DO = {
  plan_quota_boards: 'Delete a board you no longer need, or upgrade to sync more.',
  plan_quota_documents: 'Delete a document you no longer need, or upgrade to sync more.',
  plan_quota_storage: 'Delete something you no longer need, or upgrade to sync more.',
  plan_quota_document_scopes: 'Delete a workspace you no longer need, or upgrade to sync more.',
};

export function quotaCode(error) {
  const text = String(error || '');
  return Object.keys(LIMIT_TEXT).find((code) => text.includes(code)) || null;
}

export function isQuotaError(error) {
  return quotaCode(error) != null;
}

/**
 * `where` says what happened to the change: on desktop it stays on the device,
 * on the web app (no device storage) it was not saved.
 */
export function quotaMessage(error, { web = false } = {}) {
  const code = quotaCode(error);
  if (!code) return null;
  const outcome = web
    ? 'This change was not saved.'
    : 'This change is saved on this device only.';
  return `${LIMIT_TEXT[code]} ${outcome} ${WHAT_TO_DO[code]}`;
}

/** Largest file per plan when the server has not sent its limits yet (backend Plans.FileQuota). */
const PLAN_MAX_FILE_BYTES = { free: 5 * 1024 * 1024, pro: 25 * 1024 * 1024, team: 25 * 1024 * 1024, enterprise: 50 * 1024 * 1024 };

/** The largest file this account may upload: the server's figure, else the plan default. */
export function planMaxFileBytes(user) {
  const fromServer = user?._usage?.limits?.file_max_bytes;
  if (typeof fromServer === 'number' && fromServer > 0) return fromServer;
  return PLAN_MAX_FILE_BYTES[user?.plan] ?? PLAN_MAX_FILE_BYTES.free;
}

/** "1.2 MB" style, for usage next to a byte limit. */
export function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  // "5 MB", not "5.0 MB": a trailing ".0" reads like a measurement, not a limit.
  const round = (v) => (Number.isInteger(v) ? String(v) : v.toFixed(1));
  if (n < 1024 * 1024 * 1024) return `${round(n / (1024 * 1024))} MB`;
  return `${round(n / (1024 * 1024 * 1024))} GB`;
}
